import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const PLATFORMS = [
  { icon: 'ri-apple-fill', label: 'iOS (Swift / SwiftUI)', color: 'bg-gray-800 text-white' },
  { icon: 'ri-android-line', label: 'Android (Kotlin)', color: 'bg-teal-600 text-white' },
  { icon: 'ri-reactjs-line', label: 'React Native', color: 'bg-teal-100 text-teal-700' },
  { icon: 'ri-flutter-fill', label: 'Flutter', color: 'bg-amber-100 text-amber-700' },
];

const FEATURES = [
  { icon: 'ri-smartphone-line', title: 'Native iOS & Android', desc: 'Platform-native apps for the highest performance, deepest OS integration, and best user experience on both ecosystems.' },
  { icon: 'ri-reactjs-line', title: 'React Native Cross-Platform', desc: 'Single codebase, both platforms. React Native delivers near-native performance with dramatically faster development cycles and lower cost.' },
  { icon: 'ri-brain-line', title: 'On-Device AI Models', desc: 'Deploy TensorFlow Lite and Core ML models directly on the device for instant inference — no internet required, full privacy preserved.' },
  { icon: 'ri-notification-3-line', title: 'Push Notifications & Engagement', desc: 'Smart push campaigns, in-app messaging, deep linking, and behavior-triggered notifications to keep users engaged and returning.' },
  { icon: 'ri-wifi-off-line', title: 'Offline Sync & Local Storage', desc: 'Fully functional offline mode with intelligent data sync — users keep working even without connectivity, no data ever lost.' },
  { icon: 'ri-fingerprint-line', title: 'Biometric Authentication', desc: 'Face ID, Touch ID, and device-level biometric login for secure, frictionless user authentication aligned with your security policies.' },
  { icon: 'ri-map-pin-line', title: 'Location & Maps Integration', desc: 'GPS tracking, geo-fencing, route optimization, and rich map experiences — essential for field services, delivery, and logistics apps.' },
  { icon: 'ri-camera-line', title: 'Camera & Barcode Scanning', desc: 'High-performance camera integration with barcode/QR scanning, AR overlays, and image AI for inspection and capture use cases.' },
];

const PROCESS = [
  { num: '01', icon: 'ri-user-search-line', title: 'User Research', desc: 'We profile your end users, map their journeys, and validate assumptions before designing a single screen.' },
  { num: '02', icon: 'ri-artboard-line', title: 'UX Design & Prototype', desc: 'Interactive prototypes approved by your team before development starts — zero wasted build cycles.' },
  { num: '03', icon: 'ri-code-s-slash-line', title: 'Iterative Development', desc: 'Agile sprints with TestFlight/Play Store beta builds so you experience the real app every 2 weeks.' },
  { num: '04', icon: 'ri-store-2-line', title: 'App Store Launch', desc: 'We handle App Store & Play Store submissions, optimization, review management, and post-launch analytics.' },
];

export default function MobileApplicationsPage() {
  const featRef = useRef<HTMLElement>(null);
  const featInView = useInView(featRef);
  const processRef = useRef<HTMLElement>(null);
  const processInView = useInView(processRef);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-teal-50/70 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Link to="/services" className="text-gray-400 text-sm hover:text-teal-600 transition-colors cursor-pointer">Services</Link>
                <i className="ri-arrow-right-s-line text-gray-300" />
                <span className="text-teal-600 text-sm font-medium">Mobile Applications</span>
              </div>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">Mobile Applications</span> */}
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
                Mobile Apps That <span className="text-teal-500">Users Love</span>
              </h1>
              <p className="text-gray-500 text-xl leading-relaxed mb-8">
                Deliver seamless mobile experiences that connect users to your services anywhere, anytime — powered by AI features like smart notifications, on-device inference, and voice commands, available on iOS and Android.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['iOS & Android', 'React Native', 'AI On-Device', 'ISO 9001 Quality'].map((b) => (
                  <span key={b} className="bg-teal-50 text-teal-700 border border-teal-200 text-xs font-medium px-4 py-1.5 rounded-full">{b}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2">
                  Build My App <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/portfolio" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-gray-700 font-semibold px-7 py-3.5 rounded-lg border border-gray-200 transition-colors">
                  View Portfolio
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100">
              <div className="w-full h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=mobile%20application%20design%20iOS%20Android%20smartphone%20app%20screens%20mockup%20modern%20clean%20UI%20UX%20design%20teal%20green%20accent%20color%20professional%20tech%20business%20app%20interface%20multiple%20devices%20white%20background%20flat%20lay&width=700&height=500&seq=mobile-hero-001&orientation=landscape"
                  alt="Mobile Applications"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-700">Platforms & Frameworks</h2>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {PLATFORMS.map((p) => (
                <div key={p.label} className={`flex items-center gap-2.5 rounded-xl px-6 py-3 ${p.color}`}>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${p.icon} text-xl`} />
                  </div>
                  <span className="text-sm font-semibold">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-teal-600 py-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[{ val: '100+', label: 'Mobile Apps Built' }, { val: '5M+', label: 'Total App Downloads' }, { val: '4.8★', label: 'Average Store Rating' }, { val: '98%', label: 'On-Time Delivery' }].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl font-extrabold text-white">{s.val}</div>
                <div className="text-teal-200 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section ref={featRef} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-14 transition-all duration-700 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Capabilities</span> */}
              <h2 className="text-4xl font-extrabold text-gray-900">Everything in Your Mobile App</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  className={`bg-gray-50 border border-gray-100 rounded-2xl p-6 hover:border-teal-200 hover:bg-teal-50/20 transition-all duration-500 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-4">
                    <i className={`${f.icon} text-xl`} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section ref={processRef} className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-12 transition-all duration-700 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Our Process</span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">From Concept to App Store</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {PROCESS.map((step, i) => (
                <div
                  key={step.num}
                  className={`relative bg-white border border-gray-100 rounded-2xl p-7 hover:border-teal-200 transition-all duration-500 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="text-6xl font-extrabold text-teal-50 absolute top-4 right-6 leading-none select-none">{step.num}</div>
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-4">
                    <i className={`${step.icon} text-2xl`} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Ready to Launch Your Mobile App?</h2>
            <p className="text-teal-100 text-base mb-8">ISO-certified quality, 25 years of engineering excellence, and apps that users genuinely love.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-calendar-check-line" /> Get Free Consultation
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
